// App Services
import ImageService from './ImageService';
import WtfApi from './WtfApi';
import { getUUID } from './AppUtils';

/**
 * CropAreaService
 * - It will prepare the image url for the BE request.
 * - It will get the image crop data for the component.
 */
class CropAreaService {
  constructor() {
    /**
     * @type {Object}
     */
    this.window = window;

    /**
     * Instance of the Image service.
     * @type {ImageService}
     */
    this.imageService = new ImageService();

    /**
     * Instance of the wtf Api.
     * @type {WtfApi}
     */
    this.wtfApi = new WtfApi();
  }

  /**
   * It will get the base64 image from the binary.
   * @param  {Object} blobImage blob image object
   * @return {Promise}
   */
  getImageBase64(blobImage) {
    return new Promise((resolve, reject) => {
      const reader = new this.window.FileReader();

      reader.onload = (event) => {
        resolve(event.target.result);
      };

      reader.onerror = event => reject(event);

      reader.readAsDataURL(blobImage, 'utf-8');
    });
  }

  /**
   * It gets from the BE the image crop data.
   * @param  {String} base64ImageData image.
   * @return {Promise}
   */
  getImageCropsFromApi(base64Image) {
    // RegExp to capture the encoded data and the mime type from a dataURI
    // eslint-disable-next-line max-len
    const dataURIregEx = /:([a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+);(?:(?![a-zA-Z0-9_=-\s]+;)|[a-zA-Z0-9_=-\s]+;)(base64,)(.{1,})/;
    // Assign the encoded data to the body's image property.
    const body = {
      image: base64Image.match(dataURIregEx)[3],
    };

    return this.wtfApi.getImageCrops(body);
  }

  /**
   * It prepares the imageUrl for the request and then get the image crop data from the BE.
   * @param  {String} imageUrl object url image
   * @return {Promise}
   */
  getImageCrops(imageUrl) {
    return ImageService.getImageBlobFromImageUrl(imageUrl)
      .then(this.imageService.getImageBase64.bind(this))
      .then(this.getImageCropsFromApi.bind(this))
      .then(this.formatImageCropsResponse.bind(this));
  }

  /**
   * Formats the raw response coming from the BE. It will return a pure array of crops.
   * @param {Object} response
   * @property {Array} data.data Array of crops.
   */
  formatImageCropsResponse(response) {
    return response.data;
  }

  /**
   * This function translate the cropArea object from percentage to pixels.
   * @param {Object} cropArea crop area object data
   * @param {Number} imageWidth the width of the current image
   * @param {Number} imageHeight the height of the current image
   * @param {Object} response
   */
  translatePercentagesToPixels(cropArea, imageWidth, imageHeight) {
    return {
      x: (cropArea.x / 100) * imageWidth,
      y: (cropArea.y / 100) * imageHeight,
      width: (cropArea.width / 100) * imageWidth,
      height: (cropArea.height / 100) * imageHeight,
    };
  }

  /**
   * Transforms the raw backend response for the autocrop endpoint into a format useful
   * for the `CropArea > RegionSelect` component.
   * Backend data:
   * ```json
   * [{
   *      "text": String,  // Identified text
   *      "vertices": [{   // Vertice 0 for rectangle
   *          "x": Number, // pixels, absolute from source image
   *          "y": Number, // pixels, absolute from source image
   *      }, {             // Vertice 1 for rectangle, opposite to 0
   *          "x": Number, // pixels, absolute from source image
   *          "y": Number, // pixels, absolute from source image
   *      }]
   * }]
   * ```
   *
   * Result:
   *
   * ```json
   * [{
   *      "x": Number,        // percentage, relative to a target size
   *      "y": Number,        // percentage, relative to a target size
   *      "width": Number,    // percentage, relative to a target size
   *      "height": Number,   // percentage, relative to a target size
   *      "data": {
   *          "id": String,   // UUID for later identification of this region
   *          "text": String, // Identified text
   *      },
   * }]
   * ```
   * @param {Object[]} backendCrops       Array containing the raw backend data.
   * @param {Object} originalImageSize    Required in order to get the increase / decrease
   *                                      value compared to the target where the regions will
   *                                      be rendered.
   * @property {Number} width             Value in pixels.
   * @property {Number} height            Value in pixels.
   * @param {Object} targetImageSize      Required in order to get the increase / decrease
   *                                      value compared to the target where the regions will
   *                                      be rendered.
   * @property {Number} width             Value in pixels.
   * @property {Number} height            Value in pixels.
   * @return {Object[]}
   */
  translateBackendCropsToCropAreaRegions(backendCrops, originalImageSize, targetImageSize) {
    // How much bigger or smaller the target image in FE is compared to the original analyzed in BE
    const differenceX = targetImageSize.width / originalImageSize.width;
    const differenceY = targetImageSize.height / originalImageSize.height;

    return backendCrops.map(({ vertices, text }) => {
      // Points of reference coming from backend
      const [startingPoint, endingPoint] = vertices;

      // Based on the points, we can get the dimensions (px) of the crop from BE
      const originalWidth = endingPoint.x - startingPoint.x;
      const originalHeight = endingPoint.y - startingPoint.y;

      // Now we translate everything from original pixels to bigger or smaller for target image
      let targetX = startingPoint.x * differenceX;
      let targetY = startingPoint.y * differenceY;
      let targetWidth = originalWidth * differenceX;
      let targetHeight = originalHeight * differenceY;

      // Add 5px padding to all sides of the crop area for better font identification.
      const padding = 5;

      // Adjust the origin point of the crop area. The origin point should never exceed the image limits.
      targetX = targetX - padding > 0 ? targetX - padding : 0;
      targetY = targetY - padding > 0 ? targetY - padding : 0;

      // The padding should be applied to both sides.
      targetWidth = this.applyPadding(targetWidth, targetX, targetImageSize.width, padding * 2);
      targetHeight = this.applyPadding(targetHeight, targetY, targetImageSize.height, padding * 2);

      // The crop area expects region values to be percentages of the target image.
      return {
        x: (targetX * 100) / targetImageSize.width,
        y: (targetY * 100) / targetImageSize.height,
        width: (targetWidth * 100) / targetImageSize.width,
        height: (targetHeight * 100) / targetImageSize.height,
        data: {
          id: getUUID(),
          text,
        },
      };
    });
  }

  /**
   * Add a padding to the provided `valueToAdjust` param. The max amount to add is determined by `maxPadding`.
   *
   * @param {Number} valueToAdjust    The value to adjust.
   * @param {Number} origin           The starting point of the element.
   * @param {Number} containerValue   The value of the container where the element resides.
   * @param {Number} maxPadding       The maximum amount to add to the `valueToAdjust` param.
   *
   * @example If we need to adjust the width of a crop area, the required values are:
   *
   *      +--------------------------------------------+
   *      |             <----valueToAdjust---->        |
   *      |                                            |
   *      |<--origin--> +---------------------+        |
   *      |             |      CROP AREA      |        |
   *      |             +---------------------+        |
   *      |                                            |
   *      +--------------------------------------------+
   *
   *      <-------------- containerValue -------------->
   *
   *
   * @returns {Number} The adjusted value
   */
  applyPadding(valueToAdjust, origin, containerValue, maxPadding) {
    // Compute the maximum adjustment padding to add.
    const maxAdjustment = containerValue - (origin + valueToAdjust);

    // Apply no more than `maxPadding` to the `valueToAdjust`.
    const padding = maxAdjustment < maxPadding ? maxAdjustment : maxPadding;

    return valueToAdjust + padding;
  }
}

export default CropAreaService;

/**
* Compares the x, y, width and height properties of cropped area object sent in result API and
* object from initial crop data from BE API.
*
* @type {Boolean}
* @property {Object} initialData Object of one of the region in autocrop BE API
* @property {Object} cropAreaData Object of crop region data sent to result API
*/
export function isInitialAndCropDataSame(initialData, cropAreaData) {
  if (typeof initialData === 'undefined') {
    throw new TypeError('Param initialData is undefined');
  }
  if (typeof cropAreaData === 'undefined') {
    throw new TypeError('Param cropAreaData is undefined');
  }
  const properties = ['x', 'y', 'height', 'width'];
  properties.forEach((property) => {
    if (initialData[property] === 'undefined') {
      throw new TypeError(`Property ${property} of initialdata is undefined`);
    }
    if (cropAreaData[property] === 'undefined') {
      throw new TypeError(`Property ${property} of cropAreaData is undefined`);
    }
  });

  return (initialData.x === cropAreaData.x &&
  initialData.y === cropAreaData.y &&
  initialData.height === cropAreaData.height &&
  initialData.width === cropAreaData.width);
}

/**
* Compares the cropped region index with autocrop BE API response after multiple operations on image
* like image resize, region change, rotation and sets Annotation index.
*
* @type {Number}
* @property {Boolean} isAnyRegionDeleted Checks if any region has been deleted from FE
* @property {Number} selectedIndex Index of selected region in FE
* @property {Array} initialRegionData Initial properties of cropped regions as received from region select
* @property {Object} cropArea Cropped Area chosen for prediction
*/

export function getAnnotationIndex(
  isAnyRegionDeleted, selectedIndex,
  initialRegionData, cropArea
) {
  if (typeof initialRegionData === 'undefined') {
    throw new TypeError('Param initialRegionData is undefined');
  }
  if (typeof cropArea === 'undefined') {
    throw new TypeError('Param cropArea is undefined');
  }
  if ((isAnyRegionDeleted === true) || (isAnyRegionDeleted === undefined) ||
  (selectedIndex === -1) || (selectedIndex === undefined)) {
    let annotationIndex = -1;
    initialRegionData.forEach((initialData, index) => {
      if (isInitialAndCropDataSame(initialData, cropArea)) {
        annotationIndex = index;
      }
    });
    return annotationIndex;
  } else if (!isInitialAndCropDataSame(initialRegionData[selectedIndex], cropArea)) {
    return -1;
  }

  return selectedIndex;
}



// WEBPACK FOOTER //
// ./src/services/CropAreaService.js